import { CoordinatorService } from './coordinator.service';
import { Project } from './project';
import { Script } from './script';
import { ScriptAttachment } from './script-attachment';
import { ScriptAutoUser } from './script-auto-user';
import { ScriptComment } from './script-comment';
import { ScriptDatabin } from './script-databin';
import { ScriptDataset } from './script-dataset';
import { ScriptKeyword } from './script-keyword';
import { ScriptProject } from './script-project';
import { ScriptRatings } from './script-ratings';
import { ScriptReport } from './script-report';
import { ScriptResource } from './script-resource';
import { ScriptSubset } from './script-subset';
import { ScriptTags } from './script-tags'; 
import { ScriptItem } from './script-x-item';

export class ScriptOutput {
    #coords: CoordinatorService;

    #itemIdSet = new Set<string>();
    #datasetMap = new Map<string, ScriptDataset>();

    #keywordLs: ScriptKeyword[] = [];
    #subsetLs: ScriptSubset[] = [];

    #databinMap: Map<string, ScriptDatabin> = new Map();
    #resourceMap: Map<string, ScriptResource> = new Map();
    #commentMap: Map<string, ScriptComment> = new Map();
    #ratingsMap: Map<string, ScriptRatings> = new Map();
    #tagsMap: Map<string, ScriptTags> = new Map();
    #autouserMap: Map<string, ScriptAutoUser> = new Map();
    #attachmentMap: Map<string, ScriptAttachment> = new Map();
    #scriptMap: Map<string, Script> = new Map();

    isBackup = false;
    private _isShare = false;

    // #ratingsValuesMap: Map<string, ScriptRatings> = new Map();

    #title: string = 'Output';
    #description = '';
    #project?: ScriptProject | ScriptReport;

    constructor(coords: CoordinatorService, title?: string) {
        this.#coords = coords;
        this.#title = title?.trim() || this.#title;
    }

    get title() {
        return this.#title;
    }

    set title(value: string) {
        value = value.trim();
        if (!value) return;
        this.#title = value;
    }

    get description() {
        return this.#description;
    }

    set description(value: string) {
        value = value.trim();
        if (!value) return;
        this.#description = value;
    }

    asProject(title: string, description?: string) {
        const p = new Project(this.#coords, { title, description });
        this.addProject(p.scriptClass);
    }

    addProject(pr: ScriptProject | ScriptReport) {
        this.#project = pr;
        return this;
    }

    log(prefix = 'Output', type: 'all' | 'data' = 'all') {
        switch (type) {
            case 'data':
                return console.log(`${prefix}:`, this.payload['data']);
            default:
                console.log(`${prefix}:`, this.payload);
        }
        return this;
    }

    addDataset(dt: ScriptDataset) {
        this.#datasetMap.set(dt.id, dt);
        return this;
    }

    addItems(itmLs: string | string[] | ScriptItem | ScriptItem[] | ScriptDataset) {
        if (itmLs instanceof ScriptDataset) {
            itmLs = itmLs.items();
        } else if (typeof itmLs === 'string') {
            itmLs = [itmLs];
        } else if (!Array.isArray(itmLs)) itmLs = [itmLs];
        if (!itmLs.length) return;
        itmLs.forEach((itm) => {
            if (typeof itm !== 'string') itm = itm.id;
            this.#itemIdSet.add(itm);
        });

        return this;
    }

    addAutousers(auLs: ScriptAutoUser | ScriptAutoUser[]) {
        console.log('Adding autousers:', auLs);
        if (!Array.isArray(auLs)) auLs = [auLs];
        auLs.forEach((au) => this.#autouserMap.set(au.id, au));
        console.log('Added autousers:', Array.from(this.#autouserMap.values()));
        return this;
    }

    addDatabins(dbLs: ScriptDatabin | ScriptDatabin[]) {
        if (!Array.isArray(dbLs)) dbLs = [dbLs];
        dbLs.forEach((db) => {
            this.#databinMap.set(db.id, db);
            this.addResources(db.resourceList());
        });
        return this;
    }

    addComments(cLs: ScriptComment | ScriptComment[]) {
        if (!Array.isArray(cLs)) cLs = [cLs];
        cLs.forEach((c) => this.#commentMap.set(c.id, c));
        return this;
    }

    addScripts(scrLs: Script | Script[]) {
        if (!Array.isArray(scrLs)) scrLs = [scrLs];
        scrLs.forEach((scr) => this.#scriptMap.set(scr.id, scr));
        return this;
    }

    addAttachemnts(attLs: ScriptAttachment | ScriptAttachment[]) {
        if (!Array.isArray(attLs)) attLs = [attLs];
        attLs.forEach((att) => this.#attachmentMap.set(att.id, att));
        return this;
    }

    addResources(resLs: ScriptResource | ScriptResource[]) {
        if (!Array.isArray(resLs)) resLs = [resLs];
        resLs.forEach((res) => this.#resourceMap.set(res.id, res));
        return this;
    }

    addRatings(ratLs: ScriptRatings | ScriptRatings[]) {
        if (!Array.isArray(ratLs)) ratLs = [ratLs];
        ratLs.forEach((rat) => this.#ratingsMap.set(rat.id, rat));
        return this;
    }

    addTags(tagLs: ScriptTags | ScriptTags[]) {
        if (!Array.isArray(tagLs)) tagLs = [tagLs];
        tagLs.forEach((tag) => this.#tagsMap.set(tag.id, tag));
        return this;
    }

    addAttachments(attLs: ScriptAttachment | ScriptAttachment[]) {
        if (!Array.isArray(attLs)) attLs = [attLs];
        attLs.forEach((att) => this.#attachmentMap.set(att.id, att));
        return this;
    }

   
    addSubsets(subsetLs: ScriptSubset | ScriptSubset[]) {
        if (!Array.isArray(subsetLs)) subsetLs = [subsetLs];

        this.#subsetLs.push(...subsetLs);
        this.#subsetLs = Array.from(new Set(this.#subsetLs));
        return this;
    }

    deleteSubsets(subsetLs: ScriptSubset | ScriptSubset[]) {
        if (!Array.isArray(subsetLs)) subsetLs = [subsetLs];
        const s = new Set(this.#subsetLs);
        subsetLs.forEach((subset) => s.delete(subset));
        this.#subsetLs = Array.from(s);
        return this;
    }

    addKeywords(kwLs: ScriptKeyword | ScriptKeyword[]) {
        if (!Array.isArray(kwLs)) kwLs = [kwLs];
        this.#keywordLs.push(...kwLs);
        this.#keywordLs = Array.from(new Set(this.#keywordLs));
        return this;
    }

    deleteKeywords(kwLs: ScriptKeyword | ScriptKeyword[]) {
        if (!Array.isArray(kwLs)) kwLs = [kwLs];
        const s = new Set(this.#keywordLs);
        kwLs.forEach((kw) => s.delete(kw));
        this.#keywordLs = Array.from(s);
        return this;
    }

    get keywords() {
        return Array.from(this.#keywordLs);
    }

    dataKeywords(dct: any) {
        if (!this.#keywordLs.length) return;

        dct.keywords = this.#keywordLs.map((kw) => kw.json());
        const grpS = new Set<string>(dct.keywords.map((kw: any) => kw.groupId));
        dct.keywordGroups = Array.from(grpS).map((grpId) =>
            this.#coords.keyword$.getGroup(grpId)!.save(),
        );
        const styleS = new Set<string>(dct.keywordGroups.map((kwGrp: any) => kwGrp.styleId));
        dct.styles = Array.from(styleS).map((styleId) =>
            this.#coords.highlight$.getStyle(styleId)!.save(),
        );
    }

    dataAutousers(dct: any) {
        if (!this.#autouserMap.size) return;

        dct.autousers = Array.from(this.#autouserMap.values()).map((au) => au.json());
    }

    dataSubsets(dct: any) {
        if (!this.#subsetLs.length) return;
        dct.subsets = this.#subsetLs.map((subset) => subset.json());
    }

    get dataScripts() {
        if (!this.#scriptMap.size) return;
        return Array.from(this.#scriptMap.values()).map((scr) => scr.data);
    }

    dataItems(dct: any) {
        const hasItems: boolean =
            !!this.#itemIdSet.size ||
            !!this.#subsetLs.length ||
            !!this.#collection ||
            !!this.#resourceMap.size;
        ``;
        if (!hasItems) return;
        const itemSet = new Set<string>(this.#itemIdSet);

        this.#subsetLs?.forEach((subs) => subs.hitset().idSet.forEach((id) => itemSet.add(id)));
        this.#collection?.idSet.forEach((id) => itemSet.add(id));
        this.#resourceMap.forEach((res) => res.idLs.forEach((id) => itemSet.add(id)));
        dct.items = this.#_xitemPayload('memowl', itemSet);
        if (this._isShare) return;
        if (!this.#collection) dct.collection = Array.from(this.#coords.dataset$.main.mainIdList);
    }

    dataProject(dct: any) {
        if (!this.#project) return;
        dct.project = this.#project.json();
    }

    get #collection() {
        for (const dt of this.#datasetMap.values()) {
            if (!dt.isPreview) return dt;
        }
        return undefined;
    }

    dataCollection(dct: any) {
        if (this._isShare) {
            dct.collection = Array.from(this.#itemIdSet);
            return;
        }
        const col = this.#collection;
        if (!col) return;
        dct.collection = Array.from(col.idSet);
    }

    dataResources(dct: any) {
        if (!this.#resourceMap.size) return;
        dct.resources = Array.from(this.#resourceMap.values()).map((res) => res.json());
    }

  
    dataDatabins(dct: any) {
        if (!this.#databinMap.size) return;
        dct.databins = Array.from(this.#databinMap.values()).map((db) => db.json());
    }

    dataComments(dct: any) {
        if (!this.#commentMap.size) return;
        dct.comments = Array.from(this.#commentMap.values()).map((c) => c.json());
    }

    dataAttachments(dct: any) {
        if (!this.#attachmentMap.size) return;
        dct.attachments = Array.from(this.#attachmentMap.values()).map((att) => att.json());
    }

    dataRatings(dct: any) {
        if (!this.#ratingsMap.size) return;
        dct.ratings = Array.from(this.#ratingsMap.values()).map((rat) => rat.json());
        if (!this.isBackup) return;
        dct.ratingsNodesItems = Array.from(this.#ratingsMap.values())
            .map((rat) => rat.values())
            .filter((x) => x);
    }

    dataTags(dct: any) {
        if (!this.#tagsMap.size) return;
        dct.tags = Array.from(this.#tagsMap.values()).map((tag) => tag.json());
        if (!this.isBackup) return;
        dct.tagsNodesItems = Array.from(this.#tagsMap.values())
            .map((tag) => tag.values())
            .filter((x) => x);
    }

    dataStamp(dct: any) {
        dct.stamp = {
            originId: this.#coords.project$.currentId,
            actorId: this.#coords.user$.currentId,
            date: this.#coords.utils$.nowTimestamp,
        };
    }

    get data() {
        const dct: any = {};
        this.dataStamp(dct);
        this.dataProject(dct);
        this.dataCollection(dct);
        this.dataDatabins(dct);
        this.dataResources(dct);
        this.dataItems(dct);
        this.dataKeywords(dct);
        this.dataSubsets(dct);
        this.dataComments(dct);
        this.dataAttachments(dct);
        this.dataRatings(dct);
        this.dataTags(dct);
        this.dataAutousers(dct);
        return dct;
    }

    get payload() {
        return {
            ns: 'https://memowl.app',
            data: this.data,
            scripts: this.dataScripts,
        };
    }

    #_xitemPayload(type: 'ris' | 'memowl' = 'memowl', s?: Set<string>) {
        if (!s) s = this.#itemIdSet;
        const d$ = this.#coords.dataset$;
        const ls: any[] = [];
        s.forEach((id) => {
            let xitm = d$.main.get(id);
            if (!xitm) xitm = d$.preview.get(id);
            if (!xitm) return;
            switch (type) {
                case 'ris':
                    ls.push(xitm.exportRIS());
                    break;
                case 'memowl':
                    ls.push(xitm.save());
            }
        });
        return ls;
    }

    async doi() {
        const payload = this.#_xitemPayload()
            .map((x) => x.doi)
            .filter((x) => x)
            .join('\n');

        console.log('DOI:', payload);

        this.save({
            suffix: 'txt',
            payload,
            isZipped: false,
        });
    }

    async csv({ isTeamRT, payload }: { isTeamRT?: boolean; payload?: any } = {}) {
        let tag = 'call-csv-blob';
        if (isTeamRT) tag += '-teamRT';
        console.log(1111, tag, payload);
        const blob: Blob | undefined = (
            (await this.#coords.worker$.parser.call(
                tag,
                payload ? payload : this.#_xitemPayload(),
            )) as any
        ).payload;
        if (!blob) return;
        this.save({
            suffix: 'csv',
            blob,
            isZipped: false,
        });
    }

    async ris() {
        const ls: string[] = ['FN Clarivate Analytics Web of Science\nVR 1.0'];
        ls.push(...this.#_xitemPayload('ris'));
        this.save({ suffix: 'ris', payload: ls.join('\n\n'), isZipped: false });
    }

    async share() {
        this._isShare = true;
        const data = await this.save({ returnBlob: true, isZipped: true });
        if (!data) return;
        console.log('DDD', data);
        const { key, path, url } = (this.#project! as ScriptProject).shareLink;
        console.log('Share link:', key, path, url);
        const resp = await this.#coords.firebase$.storage.uploadBlob(path, data);

        console.log('Firebase response:', resp);
        this._isShare = false;
    }

    async save(
        {
            type,
            isZipped,
            title,
            suffix,
            payload,
            blob,
            returnBlob,
        }: {
            type?: 'json' | 'csv';
            isZipped?: boolean;
            title?: string;
            suffix?: string;
            payload?: any;
            blob?: Blob;
            returnBlob?: boolean;
        } = {
            type: 'json',
            isZipped: true,
            title: 'output',
        },
    ) {
        console.log('ScriptOutput.save called with:', { type, isZipped, title, suffix, returnBlob });
        payload = payload || this.payload;
        const cacheLs = (payload.data?.attachments || [])
            .filter((att: any) => !!att.values?.cachename)
            .map((att: any) => att.values?.cachename);

        console.log('Processing payload...');
        if (typeof payload !== 'string') payload = JSON.stringify((payload = payload));
        if (!blob) {
            if (isZipped || isZipped === undefined) {
                console.log('Compressing data...');
                let resp: any;
                if (cacheLs.length) {
                    console.log('Using cache compression...');
                    console.log('cacheLs', cacheLs);
                    console.log('payload', payload);
                    resp = (await this.#coords.worker$.manifest.call('compress-with-cache', {
                        payload,
                        cacheLs,
                    })) as any;
                } else {
                    console.log('Using standard compression...');
                    resp = (await this.#coords.worker$.manifest.call('compress', payload)) as any;
                }
                blob = resp.payload;
            } else {
                console.log('Creating blob without compression...');
                blob = new Blob([payload], {
                    type: 'application/json',
                });
            }
        }
        if (!blob) {
            console.log('No blob created, returning...');
            return;
        }

        if (returnBlob) {
            console.log('Returning blob...');
            return blob;
        }
        if (!suffix) suffix = isZipped ? 'ref' : type;

        console.log('Saving file:', `${title || 'output'}.${suffix}`);
        const fileName = `${title || 'output'}.${suffix}`;
        this.#coords.filesystem$.saveFile2(fileName, blob);
        return;
    }
}
